import { BackSide, Raycaster, MeshStandardMaterial, TetrahedronGeometry, IcosahedronGeometry, Scene, PerspectiveCamera, WebGLRenderer, Group, Vector3, MeshBasicMaterial, MathUtils, Mesh } from 'three';
import { ControlsPredict, State, Cloud, Time, Posts, Zone, Lights, Statistics, Sounds, FloatingBall, config, levels, gradTexture, loadCloudTextures } from './index';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { animate } from 'motion';
import { Text } from 'troika-three-text'
import { Api } from './../game/index';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js'
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js'
import { $, $$ } from '@/utilities/helpers';
import Swal from 'sweetalert2/dist/sweetalert2.js';

export default class Predict {
    constructor(canvas, debug) {

        //Setup
        this.debug = debug;
        this.canvas = canvas;
        this.config = { ...config };

        //Scene
        this.scene = new Scene();
        this.camera = new PerspectiveCamera(this.config.camera.perspective.fov, window.innerWidth / window.innerHeight, this.config.camera.near, this.config.camera.far);

        // Timing
        this.TIME = new Time(this);
        this.API = new Api();
        this.STATE = new State(this);

        // Initialisation
        this.setRenderer();
        this.setPasses()
        this.setBackground();
        this.setControls();
        this.setSounds();
        this.addFonts();

        // Await loaders
        this.init().then(() => {
            this.render();
            if (!this.state) {
                this.setIntro();
            } else {
                this.start();
            }
            this.setDebug();

        });
    }

    updatedPredictedGames() {
        let $games = $$('.js-game');
        let predicted_games = this.STATE.data?.kick_predictions || [];

        $games.forEach((game) => game.classList.remove('predicted'));

        predicted_games.forEach((predicted_game) => {
            let $game = $('.js-game[data-uid="' + predicted_game.game.uid + '"]');
            if ($game) $game.classList.add('predicted');
        });

    }

    resetPredcitGame() {
        $('.game-action H1').innerHTML = 'Choose another game';
        document.body.classList.add('gameSliderVisible');
        this.updatedPredictedGames();
    }

    /**
     * Set up the renderer
     */
    setRenderer() {
        this.renderer = new WebGLRenderer({
            canvas: this.canvas,
            antialias: true,
            powerPreference: 'high-performance'
        });
        this.renderer.setSize(window.innerWidth, window.innerHeight);
        this.renderer.setPixelRatio(2);
        this.renderer.setAnimationLoop(null);
        if (this.config.shadows) this.renderer.shadowMap.enabled = true;
        if (this.config.shadows) this.renderer.shadowMap.type = PCFSoftShadowMap;
    }


    /**
     * Set the debug mode
     */
    setDebug() {
        if (!this.debug) return;
        this.config.game.intro = false;
        this.config.game.debug = true;
        this.config.game.stats = true;
        this.config.game.music = false;
        this.CONTROLS = new ControlsPredict(this);
        this.STATS = new Statistics();
    }


    /**
     * Set up the passes
     */
    setPasses() {
        this.passes = {};
        this.passes.composer = new EffectComposer(this.renderer);
        this.passes.renderPass = new RenderPass(this.scene, this.camera);
        this.passes.composer.addPass(this.passes.renderPass);
    }

    log(message, ...optionalParams) {
        if (this.debug) {
            console.log(message, ...optionalParams);
        }
    }

    /**
     * Set up the controls
     */
    setControls() {
        this.controls = new OrbitControls(this.camera, this.renderer.domElement);
        this.controls.enableDamping = true;
        this.controls.minDistance = this.config.camera.distance.min;
        this.controls.maxDistance = this.config.camera.distance.max;
        this.controls.minPolarAngle = this.config.camera.polar_predict.min;
        this.controls.maxPolarAngle = this.config.camera.polar_predict.max;
        this.controls.minAzimuthAngle = this.config.camera.azimuth_predict.min;
        this.controls.maxAzimuthAngle = this.config.camera.azimuth_predict.max;

        let lastLogTime = 0;
        const logInterval = 1000; // Log every 1 second

        this.controls.addEventListener('change', () => {
            const now = Date.now();
            if (now - lastLogTime > logInterval) {
                this.log('Camera position:', this.camera.position);
                lastLogTime = now;
            }
        });


        this.controls.update();
    }


    /**
     * Set up the sound system
     */
    setSounds() {
        this.SOUNDS = new Sounds();
        if (this.config.game.music) this.SOUNDS.playNextTrack();
    }


    /**
     * Set up the background gradient
     */
    setBackground() {
        this.scene.add(new Mesh(new IcosahedronGeometry(this.config.sky.radius, this.config.sky.detail), new MeshBasicMaterial({
            map: gradTexture([this.config.sky.stops, this.config.sky.colours]),
            side: BackSide,
            depthWrite: false,
        })));

    }



    async init() {
        await this.addMeshes();
        this.setEventHandlers();
    }

    setIntro() {
        document.body.classList.add('intro');
        this.camera.position.set(0, 1, 0);
        this.controls.target.y += 21;
        this.controls.target.z += -14;
        this.controls.update();
    }

    addFonts() {

        this.zone_1_text = new Text();
        this.zone_1_text.font = this.config.constants.asset_directory + 'font.ttf'
        this.zone_1_text.text = 'ZONE 1'
        this.zone_1_text.fontSize = 1.3;
        this.zone_1_text.anchorY = 'center';
        this.zone_1_text.anchorX = 'center';
        this.zone_1_text.position.x = -9;
        this.zone_1_text.position.y = .99;
        this.zone_1_text.position.z = -27.1;
        this.zone_1_text.outlineWidth = 0.02;
        this.zone_1_text.color = 0x92CC75;
        this.zone_1_text.outlineColor = 0xffffff;
        this.zone_1_text.rotation.x = -Math.PI / 2;
        this.scene.add(this.zone_1_text);
        this.zone_1_text.sync();

        this.zone_1_points_text = new Text();
        this.zone_1_points_text.font = this.config.constants.asset_directory + 'font.ttf'
        this.zone_1_points_text.text = '500 POINTS'
        this.zone_1_points_text.fontSize = 0.5;
        this.zone_1_points_text.anchorY = 'center';
        this.zone_1_points_text.anchorX = 'center';
        this.zone_1_points_text.position.x = -9;
        this.zone_1_points_text.position.y = .99;
        this.zone_1_points_text.position.z = -25.5;
        this.zone_1_points_text.outlineWidth = 0.02;
        this.zone_1_points_text.color = 0x92CC75;
        this.zone_1_points_text.outlineColor = 0xffffff;
        this.zone_1_points_text.rotation.x = -Math.PI / 2;
        this.scene.add(this.zone_1_points_text);
        this.zone_1_points_text.sync();

        this.zone_2_text = new Text();
        this.zone_2_text.font = this.config.constants.asset_directory + 'font.ttf'
        this.zone_2_text.text = 'ZONE 2'
        this.zone_2_text.fontSize = 1.3;
        this.zone_2_text.anchorY = 'center';
        this.zone_2_text.anchorX = 'center';
        this.zone_2_text.position.y = .99;
        this.zone_2_text.position.z = -27.1;
        this.zone_2_text.outlineWidth = 0.02;
        this.zone_2_text.color = 0x92CC75;
        this.zone_2_text.outlineColor = 0xffffff;
        this.zone_2_text.rotation.x = -Math.PI / 2;
        this.scene.add(this.zone_2_text);
        this.zone_2_text.sync();

        this.zone_2_points_text = new Text();
        this.zone_2_points_text.font = this.config.constants.asset_directory + 'font.ttf'
        this.zone_2_points_text.text = '1000 POINTS'
        this.zone_2_points_text.fontSize = 0.5;
        this.zone_2_points_text.anchorY = 'center';
        this.zone_2_points_text.anchorX = 'center';
        this.zone_2_points_text.position.y = .99;
        this.zone_2_points_text.position.z = -25.5;
        this.zone_2_points_text.outlineWidth = 0.02;
        this.zone_2_points_text.color = 0x92CC75;
        this.zone_2_points_text.outlineColor = 0xffffff;
        this.zone_2_points_text.rotation.x = -Math.PI / 2;
        this.scene.add(this.zone_2_points_text);
        this.zone_2_points_text.sync();

        this.zone_3_text = new Text();
        this.zone_3_text.font = this.config.constants.asset_directory + 'font.ttf'
        this.zone_3_text.text = 'ZONE 3'
        this.zone_3_text.fontSize = 1.3;
        this.zone_3_text.anchorY = 'center';
        this.zone_3_text.anchorX = 'center';
        this.zone_3_text.position.x = 9;
        this.zone_3_text.position.y = .99;
        this.zone_3_text.position.z = -27.1;
        this.zone_3_text.outlineWidth = 0.02;
        this.zone_3_text.color = 0x92CC75;
        this.zone_3_text.outlineColor = 0xffffff;
        this.zone_3_text.rotation.x = -Math.PI / 2;
        this.scene.add(this.zone_3_text);
        this.zone_3_text.sync();

        this.zone_3_points_text = new Text();
        this.zone_3_points_text.font = this.config.constants.asset_directory + 'font.ttf'
        this.zone_3_points_text.text = '500 POINTS'
        this.zone_3_points_text.fontSize = 0.5;
        this.zone_3_points_text.anchorY = 'center';
        this.zone_3_points_text.anchorX = 'center';
        this.zone_3_points_text.position.x = 9;
        this.zone_3_points_text.position.y = .99;
        this.zone_3_points_text.position.z = -25.5;
        this.zone_3_points_text.outlineWidth = 0.02;
        this.zone_3_points_text.color = 0x92CC75;
        this.zone_3_points_text.outlineColor = 0xffffff;
        this.zone_3_points_text.rotation.x = -Math.PI / 2;
        this.scene.add(this.zone_3_points_text);
        this.zone_3_points_text.sync();


    }

    async addMeshes() {

        const loader = new GLTFLoader();
        const dracoLoader = new DRACOLoader();
        const { asset_directory } = this.config.constants;

        dracoLoader.setDecoderPath('https://www.gstatic.com/draco/v1/decoders/');
        loader.setDRACOLoader(dracoLoader);

        const [logo, floor, ring, ball, cloudTextures] = await Promise.all([
            loader.loadAsync(asset_directory + "logo-combined.glb"),
            loader.loadAsync(asset_directory + "ground-spark-alt.glb"),
            loader.loadAsync(asset_directory + "ring.glb"),
            loader.loadAsync(asset_directory + "ball.glb"),
            loadCloudTextures(asset_directory),
        ]);

        this.logo = logo.scene;
        this.logo.position.y = 20;
        this.logo.position.z = -15;
        this.floor = floor.scene;
        this.ring = ring.scene;

        this.floor.traverse((child) => {
            if (child.isMesh) {
                if (this.config.shadows) child.receiveShadow = true;
                child.castShadow = false;
            }
        })

        this.clouds = [
            ...Cloud.createField(this, cloudTextures, this.config.cloudsBack),
            ...Cloud.createField(this, cloudTextures),
            ...Cloud.createFixedField(this, cloudTextures, this.config.pitchClouds),
        ];

        this.clouds.forEach((cloud) => {
            this.scene.add(cloud.mesh);
            if (cloud.debugHelper) this.scene.add(cloud.debugHelper);
        });

        this.ball_logo = ball.scene.clone();

        this.ballLogoGroup = new Group();
        this.scene.add(this.ballLogoGroup);

        this.ballLogoGroup.add(this.ball_logo);
        this.ballLogoGroup.position.set(.55, 20.5, -14.95);
        this.ballLogoGroup.rotation.set(1.6, 2.4, -.1);
        this.ball_logo.rotation.set(0, 0, 0);
        this.ball_logo.position.set(0, -.17, 0.5);

        this.TIME.addEventListener('tick', () => {
            this.ballLogoGroup.rotation.z += 0.009;
        });


        if (window.innerWidth < 768) {
            this.logo.position.z = -18;
            this.ballLogoGroup.position.z = -18.05;
        }

        this.BALL = new FloatingBall(ball, this);
        this.POSTS = new Posts(this);
        this.ZONE1 = new Zone(this, this.config.zone1);
        this.ZONE2 = new Zone(this, this.config.zone2);
        this.ZONE3 = new Zone(this, this.config.zone3);
        this.LIGHTS = new Lights(this);

        this.scene.add(this.logo, this.floor, this.ring, this.BALL.object);
    }

    calculateWindForce(direction, intensity) {
        const rad = MathUtils.degToRad(direction > 180 ? 180 : 0);
        return new Vector3(Math.cos(rad) * intensity, 0, Math.sin(rad) * intensity);
    }

    getRandom(min, max) {
        return Math.random() * (max - min) + min;
    }

    reset(position, adjust_camera = true) {


        if (!position) position = this.config.positions.predict;

        if (this.level_data) position = this.level_data.position;

        if (adjust_camera) {
            this.controls.target.copy(new Vector3(position.x, position.y, position.z)).y += 0.5;
            this.controls.update();
        }

        this.hasHit = false;

        this.isHandled = false;

    }

    startPredictGame(game_uid, division) {
        return this.API.post('/start_predict_game', { game_uid, division });
    }

    start() {

        document.body.classList.remove('teamSliderVisible');
        document.body.classList.remove('gameSliderVisible');
        document.body.classList.remove('versusSliderVisible');
        document.body.classList.remove('intro');
        document.body.classList.remove('genderSliderVisible');
        document.body.classList.add('started');
        document.body.classList.remove('teamSliderVisible');

        console.log(`%c Starting the game`, 'color: #A6E3A1');

        const currentGameSlide = swipeGame.slides[swipeGame.activeIndex];
        this.division = currentGameSlide.dataset.division;
        document.querySelector('.js-division-element').innerHTML = this.division || '';


        const currentVersusSlide = swipeversus.slides[swipeversus.activeIndex];
        this.selected_team = currentVersusSlide.dataset.team;

        document.querySelector('.js-predicted-team-element').innerHTML = this.selected_team || '';

        if (window.innerWidth < 768) {
            this.tween(this.controls.target, 'y', 21, 12, 2, 'easeOut');
            this.tween(this.controls.target, 'z', -14, 0, 2, 'easeOut');
            this.tween(this.camera.position, 'y', 21, 22, 2, 'easeOut');
            this.tween(this.camera.position, 'z', -10, 40, 2, 'easeOut');
        } else {
            this.tween(this.controls.target, 'y', 21, 12, 2, 'easeOut');
            this.tween(this.controls.target, 'z', -14, -10, 2, 'easeOut');
            this.tween(this.camera.position, 'y', 21, 22, 2, 'easeOut');
            this.tween(this.camera.position, 'z', -10, 20, 2, 'easeOut');
        }

    }


    tween(object, property, start, end, duration, easing = 'linear') {
        if (easing === 'springy') {
            easing = [0.39, 2.24, 2.3, 1]
        }

        animate(start, end, {
            duration,
            easing: easing,
            onUpdate: (latest) => object[property] = latest
        });
    }

    setEventHandlers() {

        this.raycaster = new Raycaster();
        this.pointer = { x: 0, y: 0 };
        this.hovered_zone = null;

        let downedZone = null;

        // Helper function to get coordinates from event
        const getEventCoordinates = (event) => {
            if (event.touches && event.touches.length > 0) {
                return { clientX: event.touches[0].clientX, clientY: event.touches[0].clientY };
            }
            return { clientX: event.clientX, clientY: event.clientY };
        };

        // Helper function to update pointer position
        const updatePointer = (event) => {
            const coords = getEventCoordinates(event);
            this.pointer.x = (coords.clientX / window.innerWidth) * 2 - 1;
            this.pointer.y = -(coords.clientY / window.innerHeight) * 2 + 1;
        };

        // Track zone on mousedown/touchstart
        const handleStart = (event) => {
            event.preventDefault();
            updatePointer(event);
            downedZone = this.hovered_zone;
        };

        // Handle mouse/touch move
        const handleMove = (event) => {
            event.preventDefault();
            updatePointer(event);
        };

        // Handle mouseup/touchend
        const handleEnd = (event) => {
            event.preventDefault();

            if (document.body.matches('.swal2-shown, .intro, .gameSliderVisible, .versusSliderVisible')) return;

            if (this.hovered_zone && this.hovered_zone === downedZone) {
                let zones = [this.ZONE1, this.ZONE2, this.ZONE3].filter(Boolean);
                zones.forEach(zone => {
                    zone.box.material = zone.material;
                    zone.outline.material.color.set(0xebebeb);
                    zone.selected = false;
                });
                const zone = this.hovered_zone;
                zone.box.material = zone.selected_material;
                zone.selected = true;
                zone.outline.material.color.set(0x357EC0);

                function randomColor() {
                    const colors = [0x86C6FF, 0x33455C];
                    const randomIndex = Math.floor(Math.random() * colors.length);
                    const color = colors[randomIndex];
                    return color;
                }

                for (let i = 0; i < 100; i++) {
                    const geom = new TetrahedronGeometry(3, 0)
                    const material = new MeshStandardMaterial({
                        color: randomColor(),
                        metalness: 0,
                        roughness: 0.5,
                    });

                    const mesh = new Mesh(geom, material)

                    this.scene.add(mesh)

                    mesh.visible = true;
                    let scale = 0.018;
                    let pos = zone.group.position.clone();
                    pos.z = -30;

                    mesh.position.copy(pos)
                    mesh.scale.set(scale, scale, scale)
                    const targetX = pos.x + (-1 + Math.random() * 2) * 5
                    const targetY = pos.y + 2 + (-1 + Math.random() * 2) * 5
                    const targetZ = pos.z + (-1 + Math.random() * 2) * 5
                    const speed = 0.5 + Math.random() * 0.2

                    // Play sparkle sound
                    // this.SOUNDS.play('sparkle');

                    animate(mesh.rotation, { x: Math.random() * 12, y: Math.random() * 12 }, { duration: speed });
                    animate(mesh.scale, { x: scale * 3, y: scale * 3, z: scale * 3 }, { duration: speed });
                    animate(
                        mesh.position,
                        { x: targetX, y: targetY, z: targetZ },
                        {
                            duration: speed,
                            delay: Math.random() * 0.1,
                            easing: 'easeOut',
                            onComplete: () => {
                                this.scene.remove(mesh);
                            },
                        }
                    );
                }

                setTimeout(() => {
                    Swal.fire({
                        customClass: { container: 'fullSwal' },
                        title: 'Lock in your selection?',
                        html: `You have selected sector ${zone.zone_config.zone}! <br>You can come back before the game starts to update your selection.`,
                        showCancelButton: true,
                        confirmButtonText: 'Predict',
                    }).then((result) => {
                        if (result.isConfirmed) {

                            const currentVersusSlide = swipeversus.slides[swipeversus.activeIndex];

                            this.API.post('/predict_game', {
                                sector: zone.zone_config.zone,
                                team_name: currentVersusSlide.dataset.team,
                            }).then((response) => {
                                this.STATE.data = response.data;
                                window.Toast.fire({ title: `Success!`, text: response.message, timer: 3000, icon: 'success' });
                                setTimeout(() => {
                                    this.resetPredcitGame();
                                }, 3500);
                            }).catch((error) => {
                                console.log(error)
                            })
                        }
                    })

                }, 765);
            }

            // Reset downed zone
            downedZone = null;
        };

        // Mouse events
        window.addEventListener('mousedown', handleStart);
        window.addEventListener('mousemove', handleMove);
        window.addEventListener('mouseup', handleEnd);

        // Touch events
        this.canvas.addEventListener('touchstart', handleStart, { passive: false });
        this.canvas.addEventListener('touchmove', handleMove, { passive: false });
        this.canvas.addEventListener('touchend', handleEnd, { passive: false });

        let interval = null;

        window.addEventListener('blur', () => {
            const steps = 10;
            const titles = Array.from({ length: steps }, (_, i) => `${'_'.repeat(i)}🏉${'_'.repeat(steps - i - 1)}🏆`).concat('___________✅');
            let count = 0;

            interval = setInterval(() => {
                document.title = titles[count % titles.length];
                count++;
            }, 333);
        });

        window.addEventListener('focus', () => {
            document.title = 'Gallagher Golden Goals';
            clearInterval(interval);
            interval = null;
        });

        window.addEventListener('resize', () => {
            const width = window.innerWidth;
            const height = window.innerHeight;
            this.renderer.setSize(width, height);

            this.camera.aspect = width / height;
            this.camera.updateProjectionMatrix();
        });

        let $start = document.querySelector('.js-start-predict');

        if ($start) {
            $start.addEventListener('click', (e) => {
                e.preventDefault();
                this.start();
            });
        }
    }

    render() {
        if (this.config.game.stats) this.STATS.begin();

        this.raycaster.setFromCamera(this.pointer, this.camera);

        const zoneGroups = [this.ZONE1, this.ZONE2, this.ZONE3];
        const zones = [this.ZONE1?.box, this.ZONE2?.box, this.ZONE3?.box].filter(Boolean);
        const intersects = this.raycaster.intersectObjects(zones);

        document.body.classList.remove('hovering');

        if (intersects.length > 0) {

            document.body.classList.add('hovering');

            let zone = intersects[0].object.name;
            const hovered = this[zone];


            if (this.hovered_zone !== hovered) {
                zoneGroups.forEach(zoneMesh => {
                    if (zoneMesh.group.position) this.tween(zoneMesh.group.position, 'y', zoneMesh.group.position.y, 0, 0.2, 'springy');
                });
                this.tween(hovered.group.position, 'y', hovered.group.position.y, 0.4, 0.2, 'springy');

                // Play sparkle sound
                this.SOUNDS.play('woosh_in');

                this.hovered_zone = hovered;
                this.BALL.group.position.set((hovered.zone_config.position.x), 2, -29);
            }
        } else if (this.hovered_zone) {
            zoneGroups.forEach(zoneMesh => {
                if (zoneMesh.group.position) this.tween(zoneMesh.group.position, 'y', zoneMesh.group.position.y, 0, 0.2, 'springy');
            });
            this.hovered_zone = null;
        }

        const currentTime = performance.now();

        this.last_time = currentTime;

        if (!this.isDragging) this.controls.update();

        this.ring.position.set(this.BALL.object.position.x, this.ring.position.y, this.BALL.object.position.z);

        const cameraDirection = new Vector3();

        this.camera.getWorldDirection(cameraDirection);

        this.passes.composer.render()

        if (this.config.game.stats) this.STATS.end();

        this.renderer.setAnimationLoop(() => this.render());

    }

    handlePick() {


    }

}